import React, { Suspense, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/shared/lib/utils';

import { useUpdatePrivate } from '../hooks/usePlanMutations';
import { taskRefOf } from '../hooks/taskRef';
import type { TaskDetailsData } from '../types/planner';

const RichTextEditor = React.lazy(() => import('@/shared/components/richtext/RichTextEditor'));

const SAVE_DELAY_MS = 1000;

interface TaskNotesProps {
  details: TaskDetailsData;
  /** Grow the notes box into the free space of a flex column. */
  fill?: boolean;
}

export const TaskNotes: React.FC<TaskNotesProps> = ({ details, fill = false }) => {
  const { t } = useTranslation('planner');
  const update = useUpdatePrivate();
  const ref = taskRefOf(details);
  const box = useRef<HTMLDivElement>(null);
  const pending = useRef<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout>>();
  const save = useRef(() => {});
  save.current = () => {
    clearTimeout(timer.current);
    if (pending.current === null) return;
    update.mutate({ ref, data: { notes: pending.current } });
    pending.current = null;
  };

  useEffect(() => () => save.current(), []);

  const focusEditor = (event: React.MouseEvent) => {
    if (event.target !== event.currentTarget) return;
    event.preventDefault();
    box.current?.querySelector<HTMLElement>('[contenteditable="true"]')?.focus();
  };

  return (
    <div className={cn('flex flex-col gap-2', fill && 'flex-1')}>
      {details.instructions && (
        <div className="rounded-xl bg-theme-card-hover px-3.5 py-2.5 text-[14px] leading-[1.6] text-theme-text-secondary">
          <span className="mb-0.5 block text-[12px] font-bold text-theme-text-tertiary">{t('panel.instructions')}</span>
          <p dir="auto" className="whitespace-pre-line">
            {details.instructions}
          </p>
        </div>
      )}
      <div
        ref={box}
        role="presentation"
        className={cn(
          'rounded-xl border border-theme-border-light transition-colors focus-within:border-theme-accent-primary',
          fill && 'min-h-[120px] flex-1 cursor-text',
        )}
        onMouseDown={fill ? focusEditor : undefined}
        onBlur={() => save.current()}
      >
        <Suspense fallback={<div className="min-h-[52px]" />}>
          <RichTextEditor
            value={details.notes ?? ''}
            onChange={(markdown) => {
              pending.current = markdown;
              clearTimeout(timer.current);
              timer.current = setTimeout(() => save.current(), SAVE_DELAY_MS);
            }}
            placeholder={t('panel.myNotes')}
            ariaLabel={t('panel.notes')}
            textDirection="auto"
            className="min-h-[52px] px-3.5 py-2.5 text-[14px]"
          />
        </Suspense>
      </div>
    </div>
  );
};
